import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

export interface Viewport {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  /** The key directional light — environment.ts aims it / drives its shadow. */
  keyLight: THREE.DirectionalLight;
  render: () => void;
  /** Letterbox the viewport to a target aspect (w/h), or null to fill. */
  setCropAspect: (aspect: number | null) => void;
  /**
   * Restrict the 3D render to a sub-rectangle of the canvas (normalized 0–1, y
   * from the top), e.g. the left half in a split view; null = the whole canvas.
   */
  setRenderRegion: (region: RenderRegion | null) => void;
  /** Resize the renderer to an exact pixel size for export; returns a restore fn. */
  beginExport: (width: number, height: number) => () => void;
  /**
   * Render with a transparent clear instead of the opaque background, so the 3D
   * composites over the reference video below it (greenscreen-style). Ignored
   * while exporting — the video is a DOM layer that doesn't exist in exports.
   */
  setTransparentBackground: (on: boolean) => void;
  /**
   * MP4 composite export: honour the transparent background even during
   * beginExport, because the exporter itself draws the video under the frames.
   */
  setCompositeExport: (on: boolean) => void;
}

export interface RenderRegion {
  x: number;
  y: number;
  w: number;
  h: number;
}

const LETTERBOX_COLOR = 0x000000;

/**
 * Build the renderer, perspective camera, lights and OrbitControls, and wire up
 * resize handling. The viewport fills its container (#viewport) rather than the
 * whole window so grid/FOV changes never cause layout shifts (SPEC §6).
 */
export function createViewport(canvas: HTMLCanvasElement): Viewport {
  const container = canvas.parentElement as HTMLElement;

  // preserveDrawingBuffer lets the MP4 exporter read each rendered frame back
  // reliably (VideoFrame capture) without racing the compositor. alpha lets the
  // canvas go transparent for the 3D-over-video composite; with the normal
  // opaque scene.background it renders identically.
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    preserveDrawingBuffer: true,
    alpha: true,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(container.clientWidth, container.clientHeight, false);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  // Shadow maps stay enabled (toggling re-compiles materials); the ground-shadow
  // feature switches the key light's castShadow instead.
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x14171c);

  const camera = new THREE.PerspectiveCamera(
    45,
    container.clientWidth / container.clientHeight,
    0.1,
    1000
  );
  camera.position.set(0, 2, 5);

  // Lighting: soft ambient fill + key directional.
  const ambient = new THREE.AmbientLight(0xffffff, 0.6);
  scene.add(ambient);

  const key = new THREE.DirectionalLight(0xffffff, 1.4);
  key.position.set(4, 8, 6);
  scene.add(key);

  const rim = new THREE.DirectionalLight(0x88aaff, 0.4);
  rim.position.set(-6, 4, -4);
  scene.add(rim);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.05;
  controls.target.set(0, 1, 0);
  controls.update();

  let cropAspect: number | null = null;
  let splitRegion: RenderRegion | null = null;
  let exporting = false;
  let transparentBg = false;
  let compositeExport = false;

  /** The portion of the canvas the 3D may use (split half, or the whole thing). */
  function availableSize(w: number, h: number): { aw: number; ah: number } {
    if (splitRegion) return { aw: splitRegion.w * w, ah: splitRegion.h * h };
    return { aw: w, ah: h };
  }

  /** The aspect the camera should use right now (crop wins, else the available area). */
  function effectiveAspect(): number {
    if (exporting) return camera.aspect; // set explicitly by beginExport
    if (cropAspect) return cropAspect;
    const { aw, ah } = availableSize(container.clientWidth, container.clientHeight);
    return aw / ah;
  }

  function resize(): void {
    if (exporting) return;
    const w = container.clientWidth;
    const h = container.clientHeight;
    camera.aspect = effectiveAspect();
    camera.updateProjectionMatrix();
    renderer.setSize(w, h, false);
  }

  const ro = new ResizeObserver(() => resize());
  ro.observe(container);
  window.addEventListener('resize', resize);

  const renderSize = new THREE.Vector2();

  function render(): void {
    // Drive the GL viewport from the renderer's own size, not the container —
    // during export the drawing buffer is the (larger) export resolution.
    renderer.getSize(renderSize);
    const w = renderSize.x;
    const h = renderSize.y;

    // Greenscreen-style composite: clear to transparent instead of the opaque
    // background so the video layer below shows through. Never during export —
    // unless the exporter is compositing the video itself (setCompositeExport).
    const transparent = transparentBg && (!exporting || compositeExport);
    const prevBackground = scene.background;
    if (transparent) {
      scene.background = null;
      renderer.setClearColor(LETTERBOX_COLOR, 0);
    }

    try {
      if (exporting || (cropAspect === null && splitRegion === null)) {
        renderer.setScissorTest(false);
        renderer.setViewport(0, 0, w, h);
        renderer.render(scene, camera);
        return;
      }

      // Available rect (split half or full canvas), in GL coords (y from bottom).
      let ax = 0;
      let ay = 0;
      let aw = w;
      let ah = h;
      if (splitRegion) {
        ax = splitRegion.x * w;
        aw = splitRegion.w * w;
        ah = splitRegion.h * h;
        ay = (1 - splitRegion.y - splitRegion.h) * h;
      }

      // Letterbox within the available rect to the crop aspect, if any.
      let vx = ax;
      let vy = ay;
      let vw = aw;
      let vh = ah;
      if (cropAspect) {
        if (aw / ah > cropAspect) {
          vh = ah;
          vw = ah * cropAspect;
        } else {
          vw = aw;
          vh = aw / cropAspect;
        }
        vx = ax + (aw - vw) / 2;
        vy = ay + (ah - vh) / 2;
      }

      renderer.setScissorTest(false);
      renderer.setViewport(0, 0, w, h);
      renderer.setClearColor(LETTERBOX_COLOR, transparent ? 0 : 1);
      renderer.clear(); // black outside the render rect (covered by the video in splits)
      renderer.setViewport(vx, vy, vw, vh);
      renderer.setScissor(vx, vy, vw, vh);
      renderer.setScissorTest(true);
      renderer.render(scene, camera); // autoclears the rect to scene.background
      renderer.setScissorTest(false);
    } finally {
      if (transparent) scene.background = prevBackground;
    }
  }

  function setCropAspect(aspect: number | null): void {
    cropAspect = aspect;
    resize();
  }

  function setRenderRegion(region: RenderRegion | null): void {
    splitRegion = region;
    resize();
  }

  function beginExport(width: number, height: number): () => void {
    const prevPixelRatio = renderer.getPixelRatio();
    const prevSize = new THREE.Vector2();
    renderer.getSize(prevSize);
    const prevAspect = camera.aspect;

    exporting = true;
    renderer.setPixelRatio(1);
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();

    return () => {
      exporting = false;
      renderer.setPixelRatio(prevPixelRatio);
      renderer.setSize(prevSize.x, prevSize.y, false);
      camera.aspect = prevAspect;
      camera.updateProjectionMatrix();
    };
  }

  return {
    renderer,
    scene,
    camera,
    controls,
    keyLight: key,
    render,
    setCropAspect,
    setRenderRegion,
    beginExport,
    setTransparentBackground: (on) => {
      transparentBg = on;
    },
    setCompositeExport: (on) => {
      compositeExport = on;
    },
  };
}
